import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';

function CustomerForm({ companyId }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    customer_no: '',
    name: '',
    salesperson_code: '',
    balance: '',
    due_date_text: ''
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [salespersons, setSalespersons] = useState([]);

  useEffect(() => {
    if (isEdit) {
      fetchCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (companyId) {
      fetchSalespersons();
    }
  }, [companyId]);

  const fetchCustomer = async () => {
    try {
      const response = await api.get(`/customers/${id}`);
      const customer = response.data;
      setFormData({
        customer_no: customer.customer_no || '',
        name: customer.name || '',
        salesperson_code: customer.salesperson_code || '',
        balance: customer.balance || '',
        due_date_text: customer.due_date_text || ''
      });
    } catch (err) {
      setError('Failed to load customer.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalespersons = async () => {
    try {
      const response = await api.get(`/companies/${companyId}/salespersons`);
      setSalespersons(response.data);
    } catch (err) {
      console.error('Failed to fetch salespersons:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        await api.put(`/customers/${id}`, formData);
      } else {
        await api.post('/customers', formData);
      }
      navigate('/customers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading customer...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>{isEdit ? 'Edit Customer' : 'Add Customer'}</h1>
      </div>

      <div className="card">
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Customer No.</label>
              <input
                type="text"
                name="customer_no"
                className="form-control"
                value={formData.customer_no}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Salesperson</label>
              <select
                name="salesperson_code"
                className="form-control"
                value={formData.salesperson_code}
                onChange={handleChange}
              >
                <option value="">-- Select Salesperson --</option>
                {salespersons.map(sp => (
                  <option key={sp.id} value={sp.code}>
                    {sp.name} ({sp.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Due Date</label>
              <input
                type="text"
                name="due_date_text"
                className="form-control"
                value={formData.due_date_text}
                onChange={handleChange}
                placeholder="e.g. 2024-08-31"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Balance</label>
              <input
                type="number"
                step="0.01"
                name="balance"
                className="form-control"
                value={formData.balance}
                onChange={handleChange}
              />
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Customer'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CustomerForm;
